import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowLeft, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getSurveyReport, type ReportAnchorRow } from '@/lib/admin/surveys-api';

function AnchorRow({ a }: { a: ReportAnchorRow }) {
  return (
    <div className="border border-border rounded-md p-4 bg-card space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{a.name}</span>
            {a.suppressed ? (
              <Badge variant="outline">suppressed (n&lt;5)</Badge>
            ) : (
              <Badge variant="secondary">n = {a.n}</Badge>
            )}
          </div>
          {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading">
            {a.suppressed || a.mean_score == null ? '—' : a.mean_score.toFixed(2)}
          </div>
          <div className="text-[11px] text-muted-foreground">
            target {a.north_vision_target ?? '—'}
            {a.gap_from_target != null && (
              <span className={a.gap_from_target < 0 ? 'text-destructive ml-2' : 'text-primary ml-2'}>
                gap {a.gap_from_target > 0 ? '+' : ''}{a.gap_from_target.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSurveyReport() {
  const { surveyId } = useParams<{ surveyId: string }>();
  useOutletContext<{ orgId: string }>();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['admin', 'survey-report', surveyId],
    queryFn: () => getSurveyReport(surveyId!),
    enabled: !!surveyId,
  });

  if (q.isLoading) return <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />;
  if (q.error) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;

  const { survey, anchors, generated_at } = q.data;
  const visible = anchors.filter((a) => !a.suppressed);
  const overall =
    visible.length > 0
      ? visible.reduce((acc, a) => acc + (a.mean_score ?? 0), 0) / visible.length
      : null;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/admin/surveys/${surveyId}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to editor
        </Button>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={`/app/admin/surveys/${surveyId}/analytics`}>
              <BarChart3 className="h-4 w-4 mr-2" /> Analytics
            </Link>
          </Button>
          {/* TODO: rebuild PDF export against pulse_surveys */}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-semibold">{survey.name}</h2>
        <p className="text-sm text-muted-foreground">
          Report generated {new Date(generated_at).toLocaleString()}. k&lt;5 cells are suppressed.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading">Overview</CardTitle>
          <CardDescription>
            {visible.length} of {anchors.length} anchor{anchors.length === 1 ? '' : 's'} have enough responses to display.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-baseline gap-6">
          <div>
            <div className="text-3xl font-heading">{overall == null ? '—' : overall.toFixed(2)}</div>
            <div className="text-xs text-muted-foreground">average score (visible)</div>
          </div>
          <div>
            <div className="text-3xl font-heading">{anchors.reduce((a, b) => a + b.n, 0)}</div>
            <div className="text-xs text-muted-foreground">total responses</div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {anchors.length === 0 ? (
          <p className="text-sm text-muted-foreground">No anchors yet.</p>
        ) : (
          anchors.map((a) => <AnchorRow key={a.anchor_id} a={a} />)
        )}
      </div>
    </div>
  );
}
